import { useMemo, useCallback } from "react";
import {
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  type EntitySelectorConfig,
  type EntitySelection,
  type EntityItemConfig,
  type EntityPoolConfig,
  type IconName,
} from "@salestrak/ui";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { stageToPoolItem } from "#app/components/entity-selectors/hooks/use-stage-pool";
import type { Omittable } from "@salestrak/ui";

const POOL_ID = "filter-stages";

type StageInputProps = Omittable<{
  value: unknown;
  onChange: (value: unknown) => void;
  icon?: IconName;
  /** Stretch the trigger to fill its container. */
  fill?: boolean;
}>;

export function StageInput({ value, onChange, icon, fill }: StageInputProps) {
  const { stages, workflowMap } = useWorkspaceData();

  const items = useMemo<EntityItemConfig[]>(
    () =>
      stages.map((stage) =>
        stageToPoolItem(
          stage,
          (stage.workflowId
            ? workflowMap.get(stage.workflowId)?.name
            : undefined) ?? undefined,
        ),
      ),
    [stages, workflowMap],
  );

  const pool = useMemo<EntityPoolConfig>(
    () => ({ id: POOL_ID, label: "Stages", items }),
    [items],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 0,
      searchPlaceholder: "Search stages...",
      emptyMessage: "No stages found",
      noItemsMessage: "No stages available",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    // Narrowed inside the memo: a `[]` fallback built outside it is a new
    // array every render, and the memo would never hold.
    const selectedIds = Array.isArray(value) ? (value as string[]) : [];
    const itemMap = new Map(items.map((item) => [item.id, item]));
    return selectedIds
      .map((id) => {
        const entity = itemMap.get(id);
        if (!entity) return null;
        return { id, poolId: POOL_ID, entity };
      })
      .filter(Boolean) as EntitySelection[];
  }, [value, items]);

  const handleChange = useCallback(
    (next: EntitySelection[]) => {
      const ids = next.map((s) => s.id);
      onChange(ids.length > 0 ? ids : null);
    },
    [onChange],
  );

  return (
    <EntitySelector config={config} value={selections} onValueChange={handleChange}>
      <EntitySelectorTrigger
        placeholder="Select stages..."
        size="sm"
        chips
        icon={icon}
        fill={fill}
      />
      <EntitySelectorContent fit>
        <EntitySelectorInput />
        <EntitySelectorPool value={POOL_ID} />
        <EntitySelectorEmpty>No stages found</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}
